<template>
  <q-input
    v-model.trim="textInputDateStr"
    v-bind="$attrs"
    :class="{ 'borderless': readOnly }"
    :placeholder="$t(`isoDateInput.${dateDisplayFormatKey}.placeholder`)"
    :mask="localizedTextInputMask"
    :rules="inputRules"
    :readonly="readOnly"
    data-test-id="iso-date-input"
  >
    <slot />
    <template
      v-if="!readOnly"
      #append
    >
      <q-icon
        name="event"
        class="cursor-pointer"
      >
        <q-popup-proxy
          v-model="isShowingDatePicker"
          transition-show="scale"
          transition-hide="scale"
        >
          <q-date
            v-model="datePickerDateStr"
            v-bind="computedDatePickerProps"
            :mask="localizedDateDisplayFormat"
            :options="optionsFn"
          >
            <div class="row items-center justify-end">
              <q-btn
                v-close-popup
                label="Close"
                color="primary"
                flat
              />
            </div>
          </q-date>
        </q-popup-proxy>
      </q-icon>
    </template>
  </q-input>
</template>

<script>
import { IsoDate } from 'src/models/Model'

// This component accepts all QInput props and by default emits an IsoDate object
// in the browser's local timezone, not in UTC time zone.
export default {
  name: 'IsoDateInput',
  // Allows manually forwarding `$attrs` to QInput.
  inheritAttrs: false,
  props: {
    // **IMPORTANT:** If you pass in a date string, the format must match `dateStringDataFormat`,
    // default is 'YYYY-MM-DD' (ISO 8601).
    modelValue: {
      type: [IsoDate, Date, String],
      required: false,
      default () {
        return null
      }
    },
    // This key points to the format used to display dates to the user. This is what the user sees and types.
    dateDisplayFormatKey: {
      type: String,
      required: false,
      default: 'shortDate'
    },
    // This format is used when parsing/formatting dates that are received/emitted across our components/models.
    // This format does not determine how dates are displayed to the user; this only affects data flow.
    dateStringDataFormat: {
      type: String,
      required: false,
      default: 'YYYY-MM-DD' // ISO 8601
    },
    // When option is `null` (default), values are emitted as IsoDate objects. When option is `'nullify-blank'`,
    // valid dates are emitted as formatted string values, while invalid/blank values are emitted as `null`.
    // When option is `'allow-blank'`, valid dates are emitted as formatted string values, while invalid/blank
    // dates are emitted as empty strings.
    emitString: {
      type: String,
      required: false,
      default: null
    },
    datePickerProps: {
      type: Object,
      required: false,
      default () {
        return {}
      }
    },
    hideDatePickerOnInput: {
      type: Boolean,
      required: false,
      default: true
    },
    // The 'last-valid-value' option (default) causes the component to only emit
    // a new date value when the user has finished typing a complete
    // and valid date value. In other words, if the user begins typing or
    // deleting characters on an incomplete date string, the last valid
    // date value (if any) will be remembered until the user has finished
    // inputting a new full complete value, or until the user has
    // completely cleared it.
    //
    // The 'nullify' option causes the component to immediately emit a `null`
    // value as soon as the user inputs an incomplete date string. For example
    // while they're typing or deleting characters.
    //
    // The 'parse-partial' option causes the component to immediately emit
    // new date values as the user inputs *any* string values, even
    // incomplete dates strings, as soon as Quasar's date.extractDate() util
    // can determine some date. This is not recommended because partial date
    // strings can result in Date objects with unexpected default values. Examples:
    //
    // - `extractDate('', 'MM/DD/YYYY')` => Sun Dec 31 1899 00:00:00 (local TZ)
    // - `extractDate('06/18', 'MM/DD/YYYY')` => Sun Dec 31 1899 00:00:00 (local TZ)
    // - `extractDate('06/18/2', 'MM/DD/YYYY')` => Wed Jun 18 1902 00:00:00 (local TZ)
    // - `extractDate('06/18/20', 'MM/DD/YYYY')` => Fri Jun 18 1920 00:00:00 (local TZ)
    coercePartialDates: {
      type: String,
      required: false,
      default: 'last-valid-value'
    },
    /**
     * Function for limiting the date picker options.
     */
    optionsFn: {
      type: Function,
      required: false,
      default: undefined
    },
    readOnly: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['update:model-value'],
  data () {
    return {
      textInputDateStr: '',
      datePickerDateStr: '',
      isoDateValue: null,
      isShowingDatePicker: false,
      // Must defer setting this until after mount. See mounted() lifecycle hook.
      localizedTextInputMask: undefined
    }
  },
  computed: {
    computedDatePickerProps () {
      const inheritableInputProps = _.pick(this.$attrs, ['disable', 'readonly'])
      return {
        ...this.datePickerProps,
        ...inheritableInputProps
      }
    },
    localizedDateDisplayFormat () {
      // Fallback to default string if locale key not defined
      // Comparing the translation output against the key is a hack to determine
      // if the translation is missing. There is a bug with the i18n.te() function
      // that is supposed to help with checking if key exists, but doesn't work
      // for nested keys. When a translation is missing, the key string is returned.
      const key = `isoDateInput.${this.dateDisplayFormatKey}.displayFormat`
      const isMissingTranslation = this.$i18n.t(key) === key
      if (isMissingTranslation) {
        return 'MM/DD/YYYY'
      } else {
        return this.$i18n.t(key)
      }
    },
    isoDateFormattedForUser () {
      if (!this.isoDateValue) return ''
      return this.isoDateValue.format(this.localizedDateDisplayFormat)
    },
    isoDateFormattedForApi () {
      if (!this.isoDateValue) return ''
      return this.isoDateValue.format(this.dateStringDataFormat)
    },
    inputRules () {
      return [...(this.$attrs.rules || []), this.validateCompleteDateString]
    }
  },
  watch: {
    modelValue: {
      handler (newVal) {
        if ((_.isString(newVal) && newVal === this.isoDateFormattedForApi) || newVal === this.isoDateValue) {
          return
        }

        if (_.isString(newVal) && newVal.length > 0) {
        // Translate given date string to the localized format, which is what the
        // user sees and inputs. If we emit a date string later, we translated it back.
          const maybeDate = IsoDate.parse(newVal, this.dateStringDataFormat)
          newVal = maybeDate.format(this.localizedDateDisplayFormat)
        }

        this.updateIsoDateValue(newVal)
      },
      immediate: true
    },
    textInputDateStr (newDateStr) {
      newDateStr ||= '' // Quasar `clearable` button sets value to null
      if (newDateStr === this.isoDateFormattedForUser) {
        return
      }

      // See `coercePartialDates` prop comments. Updating only on
      // valid length is the 'last-valid-value' (default) behavior.
      if (this.isValidDateStringLength(newDateStr) || this.coercePartialDates === 'parse-partial') {
        this.updateIsoDateValue(newDateStr, true)
      } else if (this.coercePartialDates === 'nullify' && this.isoDateValue !== null) {
        this.updateIsoDateValue(null, true)
      }
    },
    datePickerDateStr (newDateStr) {
      if (newDateStr === this.isoDateFormattedForUser) {
        return
      }
      // We can assume date picker format is valid as long
      // as we provided it the correct mask.
      this.updateIsoDateValue(newDateStr)
    }
  },
  mounted () {
    // There seems to be a race condition when passed in a v-model value of `null` that causes the input
    // masking function to error on initial render because of a missing initial ref due to the null value.
    // So we wait until the after the first render to set the input mask.
    this.setLocalizedTextInputMask()
  },
  methods: {
    updateIsoDateValue (dateStrOrObj, isUpdateFromTextInput = false) {
      let newIsoDate = null
      if (dateStrOrObj) {
        const maybeDate = IsoDate.parse(dateStrOrObj, this.localizedDateDisplayFormat)
        if (maybeDate.isDate()) {
          newIsoDate = maybeDate
        }
      }

      // At this point we've determined whether the user has inputted a new
      // date or decided to clear it. We re-sync QInput or QDate values below.
      this.isoDateValue = newIsoDate

      if (this.datePickerDateStr !== this.isoDateFormattedForUser) {
        this.datePickerDateStr = this.isoDateFormattedForUser
      }

      // For the text field, always re-sync if date picker triggered the update.
      // Otherwise, if the update was triggered by the user typing only change
      // their text when they reach one the following completion states:
      // - text field contains a full date string, so a new date
      // - text field contains an empty string, so a cleared date
      // This has the benefit auto-correcting full-length, but impossible dates.
      if (
        this.textInputDateStr !== this.isoDateFormattedForUser &&
        (!isUpdateFromTextInput || this.isValidDateStringLength(this.textInputDateStr))
      ) {
        this.textInputDateStr = this.isoDateFormattedForUser
      }

      if (this.hideDatePickerOnInput && !isUpdateFromTextInput) {
        this.hideDatePicker()
      }

      if (this.emitString) {
        if (this.isoDateFormattedForApi) {
          this.$emit('update:model-value', this.isoDateFormattedForApi)
        } else if (this.emitString === 'nullify-blank') {
          this.$emit('update:model-value', null)
        } else {
          this.$emit('update:model-value', '')
        }
      } else {
        this.$emit('update:model-value', this.isoDateValue)
      }
    },
    isValidDateStringLength (dateStr) {
      dateStr ||= ''
      if (dateStr.length === 0 || dateStr.length === this.localizedDateDisplayFormat.length) {
        // We treat empty input as the user intending to set a date to null.
        return true
      } else {
        // We should not allow partial date strings because they can result in valid date
        // objects with undesirable defaults. We rely on a QInput mask to limit the allowed
        // input characters and max length.
        return false
      }
    },
    validateCompleteDateString (val) {
      val ||= ''
      return this.isValidDateStringLength(val) || 'Please enter valid date'
    },
    showDatePicker () {
      this.isShowingDatePicker = true
    },
    hideDatePicker () {
      this.isShowingDatePicker = false
    },
    // QInput's `mask` prop expects different tokens compared to QDate's `mask` prop.
    // Assuming the format is simple, this translates the date format tokens to QInput's mask tokens.
    // Example: 'MM/DD/YYYY' => '##/##/####'
    // Docs: https://quasar.dev/vue-components/input#mask
    setLocalizedTextInputMask () {
      this.localizedTextInputMask = this.localizedDateDisplayFormat.replace(/[a-z]/gi, '#')
    }
  }
}
</script>
